import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import GlassFinderPerCompany from "../components/LinearDecomposer/GlassFinderPerCompany";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const VISITOR_EMAIL_KEY = "sponsor_visit_email";
const VISITOR_NAME_KEY = "sponsor_visit_name";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

async function submitSponsorVisit({ sponsorName, email, visitorName }) {
    return axiosInstance.post("/finder/company/sponsor-visit", {
        sponsor_name: sponsorName,
        email,
        visitor_name: visitorName || "",
    });
}

const SponsorManufacturer = () => {
    const { company } = useParams();
    const navigate = useNavigate();
    const manufacturer = company ? decodeURIComponent(company) : "";
    const visitSentRef = useRef("");
    const [emailPromptOpen, setEmailPromptOpen] = useState(false);
    const [emailInput, setEmailInput] = useState("");
    const [nameInput, setNameInput] = useState("");
    const [emailError, setEmailError] = useState("");

    useEffect(() => {
        if (!manufacturer) {
            return;
        }

        const storedEmail = localStorage.getItem(VISITOR_EMAIL_KEY);
        if (storedEmail) {
            const storedName =
                localStorage.getItem(VISITOR_NAME_KEY) ||
                localStorage.getItem("username") ||
                "";

            setEmailInput(storedEmail);
            setNameInput(storedName);
            if (storedName.trim()) {
                setEmailPromptOpen(false);
                void submitSponsorVisit({
                    sponsorName: manufacturer,
                    email: String(storedEmail || "").trim().toLowerCase(),
                    visitorName: storedName,
                }).catch((error) => {
                    console.error("Failed to record sponsor visit:", error);
                });
            } else {
                setEmailPromptOpen(true);
            }
            return;
        }

        setEmailInput(localStorage.getItem("email") || "");
        setNameInput(localStorage.getItem(VISITOR_NAME_KEY) || localStorage.getItem("username") || "");
        setEmailPromptOpen(true);
    }, [manufacturer]);

    const handleEmailSubmit = async (event) => {
        event.preventDefault();

        const normalizedEmail = String(emailInput || "").trim().toLowerCase();
        if (!EMAIL_RE.test(normalizedEmail)) {
            setEmailError("Please enter a valid email address.");
            return;
        }

        const normalizedName = String(nameInput || "").trim();
        if (!normalizedName) {
            setEmailError("Please enter your name to continue.");
            return;
        }

        setEmailError("");
        const visitKey = `${manufacturer.trim().toLowerCase()}::${normalizedEmail}`;
        visitSentRef.current = visitKey;

        localStorage.setItem(VISITOR_EMAIL_KEY, normalizedEmail);
        localStorage.setItem(VISITOR_NAME_KEY, normalizedName);
        setEmailPromptOpen(false);

        void submitSponsorVisit({
            sponsorName: manufacturer,
            email: normalizedEmail,
            visitorName: normalizedName,
        }).catch((error) => {
            console.error("Failed to record sponsor visit:", error);
            visitSentRef.current = "";
        });
    };

    if (!manufacturer) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100 px-4 py-16 text-center text-slate-900">
                <p className="text-lg font-semibold text-slate-950">Manufacturer not specified.</p>
                <p className="mt-2 text-sm text-slate-600">
                    Please visit the landing page and select a sponsored tool.
                </p>
                <Link
                    to="/"
                    className="mt-4 inline-flex rounded-full border border-slate-300 bg-white px-5 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                >
                    Return home
                </Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100 px-4 py-10 text-slate-900">
            <div className="mx-auto max-w-7xl rounded-[2rem] border border-slate-200 bg-white p-4 shadow-[0_24px_80px_rgba(15,23,42,0.08)] sm:p-6">
                <div className="relative mb-6 text-center pt-2">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="absolute left-0 top-0 inline-flex items-center rounded-full border border-slate-300 bg-slate-800 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-900"
                    >
                        Go back
                    </button>
                    <p className="text-xs uppercase tracking-[0.4em] text-slate-500">Sponsored tool</p>
                    <h1 className="text-3xl font-semibold tracking-tight text-slate-950">{manufacturer}</h1>
                </div>
                <GlassFinderPerCompany
                    basePath="/finder/company/sponsor"
                    initialCompany={manufacturer}
                    hideCompanySelector
                    showToastContainer={false}
                />
            </div>

            {emailPromptOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-8 backdrop-blur-sm">
                    <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-6 text-slate-900 shadow-2xl">
                        <p className="text-xs font-semibold uppercase tracking-[0.35em] text-slate-500">
                            Sponsor access
                        </p>
                        <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-950">
                            Enter your email to continue
                        </h2>
                        <p className="mt-2 text-sm leading-6 text-slate-600">
                            We use this to record visits for {manufacturer} before loading the sponsored tool.
                        </p>

                        <form className="mt-6 space-y-4" onSubmit={handleEmailSubmit}>
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-slate-700">
                                    Email address
                                </label>
                                <input
                                    type="email"
                                    value={emailInput}
                                    onChange={(event) => {
                                        setEmailInput(event.target.value);
                                        if (emailError) {
                                            setEmailError("");
                                        }
                                    }}
                                    placeholder="name@example.com"
                                    className="h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none ring-0 transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                                    autoComplete="email"
                                />
                                {emailError && (
                                    <p className="mt-2 text-sm font-medium text-rose-600">{emailError}</p>
                                )}
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-semibold text-slate-700">
                                    Name
                                </label>
                                <input
                                    type="text"
                                    value={nameInput}
                                    onChange={(event) => setNameInput(event.target.value)}
                                    placeholder="Your name"
                                    className="h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none ring-0 transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                                    autoComplete="name"
                                />
                            </div>

                            <div className="flex flex-wrap justify-end gap-3">
                                <button
                                    type="submit"
                                    className="inline-flex h-12 items-center rounded-2xl bg-slate-900 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    Continue
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <ToastContainer position="top-right" autoClose={4000} newestOnTop />
        </div>
    );
};

export default SponsorManufacturer;
